import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../Context/AuthContext'

const statusColors = {
  Processing: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Shipping: 'bg-blue-100 text-blue-800 border-blue-300',
  Collected: 'bg-green-100 text-green-800 border-green-300',
}

const Profile = () => {
  const navigate = useNavigate()
  const { user, loading, updateProfile, changePassword, getOrders, logout } = useAuth()

  const [activeTab, setActiveTab] = useState('profile')
  const [orders, setOrders] = useState([])
  const [ordersLoading, setOrdersLoading] = useState(false)

  const [addressStreet, setAddressStreet] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressProvince, setAddressProvince] = useState('')
  const [addressPostalCode, setAddressPostalCode] = useState('')
  const [coupon, setCoupon] = useState('')
  const [addressSaved, setAddressSaved] = useState(false)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState('')
  const [pwLoading, setPwLoading] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (user) {
      let addr = {}
      if (user.address) {
        try {
          addr = typeof user.address === 'string' ? JSON.parse(user.address) : user.address
        } catch {
          addr = {}
        }
      }
      setAddressStreet(addr.street || '')
      setAddressCity(addr.city || '')
      setAddressProvince(addr.province || '')
      setAddressPostalCode(addr.postalCode || '')
      setCoupon(user.coupon || '')
    }
  }, [user])

  useEffect(() => {
    if (activeTab === 'orders' && user) {
      setOrdersLoading(true)
      getOrders()
        .then(setOrders)
        .catch(() => setOrders([]))
        .finally(() => setOrdersLoading(false))
    }
  }, [activeTab, user, getOrders])

  if (loading) return null

  if (!user) {
    return (
      <div className='min-h-screen bg-[#fde1ff] flex flex-col'>
        <div className='bg-gradient-to-br from-violet-700 via-violet-800 to-indigo-900 text-white py-16 px-4 text-center'>
          <h1 className='text-4xl font-bold mb-3'>My Account</h1>
          <p className='text-lg text-violet-200 max-w-xl mx-auto'>
            Manage your profile, orders, and preferences.
          </p>
        </div>
        <div className='flex-1 flex items-center justify-center px-4 py-12'>
          <div className='bg-white rounded-xl shadow-lg p-8 w-full max-w-md text-center'>
            <h2 className='text-xl font-semibold text-gray-800 mb-3'>Sign In Required</h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please sign in or create an account to access your profile.
            </p>
            <div className='flex justify-center gap-3'>
              <Link to='/LogIn' className='rounded-lg bg-violet-800 px-6 py-2.5 text-sm font-semibold text-white hover:bg-violet-900 transition-colors'>
                Log In
              </Link>
              <Link to='/SignUp' className='rounded-lg border border-violet-300 px-6 py-2.5 text-sm font-semibold text-violet-700 hover:bg-violet-50 transition-colors'>
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleAddressSave = async () => {
    setAddressSaved(false)
    const addr = {
      street: addressStreet || '',
      city: addressCity || '',
      province: addressProvince || '',
      postalCode: addressPostalCode || '',
    }
    const hasContent = addr.street || addr.city || addr.province || addr.postalCode
    try {
      await updateProfile({ address: hasContent ? JSON.stringify(addr) : null, coupon: coupon || null })
      setAddressSaved(true)
      setTimeout(() => setAddressSaved(false), 2000)
    } catch {
    }
  }

  const handlePasswordChange = async (e) => {
    e.preventDefault()
    setPwError('')
    setPwSuccess('')

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPwError('Please fill in all fields.')
      return
    }
    if (newPassword.length < 8) {
      setPwError('New password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmNewPassword) {
      setPwError('New passwords do not match.')
      return
    }

    setPwLoading(true)
    try {
      await changePassword(currentPassword, newPassword)
      setPwSuccess('Password changed successfully.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
    } catch (err) {
      setPwError(err.message)
    } finally {
      setPwLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const tabs = [
    { key: 'profile', label: 'Profile' },
    { key: 'address', label: 'Address' },
    { key: 'password', label: 'Password' },
    { key: 'orders', label: 'Orders' },
    { key: 'coupons', label: 'Coupons' },
  ]

  return (
    <div className='min-h-screen bg-[#fde1ff] flex flex-col'>
      <div className='bg-gradient-to-br from-violet-700 via-violet-800 to-indigo-900 text-white py-16 px-4 text-center'>
        <h1 className='text-4xl font-bold mb-3'>My Account</h1>
        <p className='text-lg text-violet-200 max-w-xl mx-auto'>
          Manage your profile, orders, and preferences.
        </p>
      </div>

      <div className='flex-1 flex items-start justify-center px-4 py-12'>
        <div className='bg-white rounded-xl shadow-lg w-full max-w-2xl overflow-hidden'>
          <div className='flex border-b border-gray-200 overflow-x-auto'>
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex-1 min-w-0 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === t.key
                    ? 'text-violet-700 border-b-2 border-violet-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className='p-6'>
            {activeTab === 'profile' && (
              <div className='space-y-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Full Name</label>
                  <div className='w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800'>{user.name}</div>
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Email Address</label>
                  <div className='w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800'>{user.email}</div>
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Member Since</label>
                  <div className='w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800'>
                    {user.created_at ? new Date(user.created_at + 'Z').toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className='rounded-lg border border-red-300 px-6 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors'
                >
                  Log Out
                </button>
              </div>
            )}

            {activeTab === 'address' && (
              <div className='space-y-4'>
                <p className='text-sm text-gray-600'>
                  Save your delivery address for a faster checkout experience.
                </p>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Street Address</label>
                  <input
                    type='text'
                    value={addressStreet}
                    onChange={(e) => setAddressStreet(e.target.value)}
                    placeholder='123 Main St'
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                  />
                </div>
                <div className='grid gap-4 sm:grid-cols-3'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>City</label>
                    <input
                      type='text'
                      value={addressCity}
                      onChange={(e) => setAddressCity(e.target.value)}
                      placeholder='Cape Town'
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Province</label>
                    <input
                      type='text'
                      value={addressProvince}
                      onChange={(e) => setAddressProvince(e.target.value)}
                      placeholder='Western Cape'
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Postal Code</label>
                    <input
                      type='text'
                      value={addressPostalCode}
                      onChange={(e) => setAddressPostalCode(e.target.value)}
                      placeholder='8001'
                      className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    />
                  </div>
                </div>
                <button
                  onClick={handleAddressSave}
                  className='rounded-lg bg-violet-800 px-6 py-2.5 text-sm font-semibold text-white hover:bg-violet-900 transition-colors'
                >
                  Save Address
                </button>
                {addressSaved && (
                  <p className='text-sm text-green-600'>Address saved successfully.</p>
                )}
              </div>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handlePasswordChange} className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Current Password</label>
                  <input
                    type='password'
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    required
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>New Password</label>
                  <input
                    type='password'
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder='At least 8 characters'
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>Confirm New Password</label>
                  <input
                    type='password'
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent'
                    required
                  />
                </div>

                {pwError && (
                  <div className='bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3 border border-red-200'>{pwError}</div>
                )}
                {pwSuccess && (
                  <div className='bg-green-50 text-green-700 text-sm rounded-lg px-4 py-3 border border-green-200'>{pwSuccess}</div>
                )}

                <button
                  type='submit'
                  disabled={pwLoading}
                  className='rounded-lg bg-violet-800 px-6 py-2.5 text-sm font-semibold text-white hover:bg-violet-900 disabled:opacity-50 transition-colors'
                >
                  {pwLoading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            )}

            {activeTab === 'orders' && (
              <div className='space-y-4'>
                {ordersLoading ? (
                  <p className='text-sm text-gray-500'>Loading orders...</p>
                ) : orders.length === 0 ? (
                  <p className='text-sm text-gray-500'>You have no orders yet.</p>
                ) : (
                  orders.map(order => (
                    <div key={order.orderReference} className='border border-gray-200 rounded-lg p-4'>
                      <div className='flex items-center justify-between mb-2'>
                        <span className='text-sm font-semibold text-gray-800'>#{order.orderReference}</span>
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${statusColors[order.status] || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
                          {order.status}
                        </span>
                      </div>
                      <div className='text-xs text-gray-500 mb-2'>
                        {new Date(order.createdAt + 'Z').toLocaleDateString()}
                        {order.shippingMethod && ` · ${order.shippingMethod}`}
                      </div>
                      <div className='text-sm font-medium text-gray-700'>
                        R{(order.totalAmount / 100).toFixed(2)}
                      </div>
                      <div className='mt-2 space-y-1'>
                        {order.lineItems.map((item, i) => (
                          <div key={i} className='text-xs text-gray-600'>
                            {item.displayName} x{item.quantity}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'coupons' && (
              <div className='space-y-4'>                
                {coupon ? (
                  <div className='flex items-center gap-3'>
                    <div className='flex-1 rounded-lg border border-dashed border-violet-400 bg-violet-50 px-4 py-3 text-center'>
                      <span className='text-lg font-bold text-violet-800 tracking-wider'>{coupon}</span>
                    </div>
                  </div>
                ) : (
                  <p className='text-sm text-gray-500'>No coupon registered on your account.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Profile
